import React, { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Badge from './shared/Badge';
import Alert from './shared/Alert';
import Spinner from './shared/Spinner';
import Select from './shared/Select';

const STATUS_VARIANTS = {
    indexed: 'success',
    pending: 'warning',
    failed: 'error',
};

export default function AdminRAGIndex() {
    const [collections, setCollections] = useState([]);
    const [selectedCollection, setSelectedCollection] = useState('');
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [docsLoading, setDocsLoading] = useState(false);
    const [indexingDoc, setIndexingDoc] = useState(null);
    const [reindexing, setReindexing] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [costInfo, setCostInfo] = useState(null);

    useEffect(() => { loadCollections(); }, []);

    useEffect(() => {
        if (selectedCollection) {
            loadDocuments(selectedCollection);
        } else if (collections.length > 0) {
            loadAllDocuments();
        }
    }, [selectedCollection, collections]);

    const loadCollections = async () => {
        try {
            setLoading(true);
            const res = await adminApi.listRAGCollections();
            setCollections(res.collections || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadDocuments = async (collectionId) => {
        try {
            setDocsLoading(true);
            setError(null);
            const res = await adminApi.listRAGDocuments(collectionId);
            setDocuments(res.documents || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setDocsLoading(false);
        }
    };

    const loadAllDocuments = async () => {
        try {
            setDocsLoading(true);
            setError(null);
            const allDocs = [];
            for (const col of collections) {
                const res = await adminApi.listRAGDocuments(col.id);
                allDocs.push(...(res.documents || []));
            }
            setDocuments(allDocs);
        } catch (err) {
            setError(err.message);
        } finally {
            setDocsLoading(false);
        }
    };

    const handleIndex = async (documentId) => {
        try {
            setIndexingDoc(documentId);
            setError(null);
            setCostInfo(null);
            const result = await adminApi.indexRAGDocument(documentId);
            setSuccessMessage(`Indexed ${result.chunks_indexed} chunks (${result.status})`);
            if (result.estimated_cost) {
                setCostInfo(result.estimated_cost);
            }
            if (selectedCollection) {
                loadDocuments(selectedCollection);
            } else {
                loadAllDocuments();
            }
            setTimeout(() => { setSuccessMessage(null); setCostInfo(null); }, 8000);
        } catch (err) {
            setError(err.message);
        } finally {
            setIndexingDoc(null);
        }
    };

    const handleReindex = async () => {
        if (!selectedCollection) {
            setError('Please select a collection to reindex');
            return;
        }
        const target = selectedCollection;
        const col = collections.find((c) => c.id === target);
        if (!confirm(`Reindex all documents in "${col?.name || target}"? This will rebuild all vectors.`)) return;

        try {
            setReindexing(true);
            setError(null);
            setCostInfo(null);
            const results = await adminApi.reindexRAGCollection(target);
            const totalChunks = results.reduce((sum, r) => sum + r.chunks_indexed, 0);
            const totalCost = results.reduce((sum, r) => sum + (r.estimated_cost?.cost_usd || 0), 0);
            setSuccessMessage(`Reindexed ${results.length} documents, ${totalChunks} total chunks`);
            if (totalCost > 0) {
                setCostInfo({ cost_usd: totalCost, is_free: false });
            }
            if (selectedCollection) {
                loadDocuments(selectedCollection);
            } else {
                loadAllDocuments();
            }
            setTimeout(() => { setSuccessMessage(null); setCostInfo(null); }, 8000);
        } catch (err) {
            setError(err.message);
        } finally {
            setReindexing(false);
        }
    };

    const collectionOptions = [
        { value: '', label: 'All Collections' },
        ...collections.map((c) => ({ value: c.id, label: c.name })),
    ];

    const collectionMap = Object.fromEntries(collections.map((c) => [c.id, c.name]));

    if (loading) return <Spinner size="lg" text="Loading..." center />;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Document Index</h1>
                    <p className="text-sm text-gray-500 mt-0.5">{documents.length} documents</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select
                        value={selectedCollection}
                        onChange={(e) => setSelectedCollection(e.target.value)}
                        options={collectionOptions}
                        className="w-48"
                    />
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleReindex}
                        disabled={reindexing || !selectedCollection}
                    >
                        {reindexing ? 'Reindexing...' : 'Reindex All'}
                    </Button>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && (
                <Alert variant="success" onClose={() => { setSuccessMessage(null); setCostInfo(null); }}>
                    {successMessage}
                    {costInfo && !costInfo.is_free && (
                        <span className="ml-2 text-xs opacity-80">
                            (est. ${costInfo.cost_usd.toFixed(6)})
                        </span>
                    )}
                </Alert>
            )}

            {docsLoading ? (
                <Spinner size="lg" text="Loading documents..." center />
            ) : documents.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">
                    No documents found. Add documents to a collection first.
                </div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Title</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Collection</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Status</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Chunks</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Source</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {documents.map((doc) => (
                                <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3">
                                        <span className="font-medium text-gray-900 block truncate max-w-[240px]">{doc.title}</span>
                                        <p className="text-[10px] text-gray-400 mt-0.5 font-mono truncate max-w-[240px]">{doc.id}</p>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="text-xs text-gray-600">{collectionMap[doc.collection_id] || doc.collection_id}</span>
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                        <Badge variant={STATUS_VARIANTS[doc.status] || 'default'} dot>
                                            {doc.status}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-3 text-center text-gray-700">{doc.chunk_count}</td>
                                    <td className="px-3 py-3">
                                        <span className="text-xs text-gray-500">{doc.source_type}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <Button
                                            variant="ghost"
                                            size="xs"
                                            onClick={() => handleIndex(doc.id)}
                                            disabled={indexingDoc === doc.id}
                                        >
                                            {indexingDoc === doc.id ? 'Indexing...' : doc.status === 'indexed' ? 'Reindex' : 'Index'}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
